import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const words = ["FUTURE", "CAREERS", "SKILLS", "GROWTH"];

export default function Hero() {
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();

  const goToEnquiry = () => {
    navigate("/", { state: { scrollTo: "enquiry" } });
  };

  const goToPrograms = () => {
    navigate("/", { state: { scrollTo: "services" } });
  };

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, 2600);
    return () => clearInterval(id);
  }, []);

  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 500], [0, -80]);
  const glowY = useTransform(scrollY, [0, 500], [0, 100]);
  const bgOpacity = useTransform(scrollY, [0, 300], [1, 0.55]);

  return (
    <section className="relative min-h-auto lg:min-h-screen bg-[#020617] text-white overflow-hidden py-12 sm:py-16 md:py-20">
      <motion.div
        style={{ y: bgY, opacity: bgOpacity }}
        className="absolute inset-0 bg-gradient-to-br from-indigo-900/35 via-black to-cyan-900/25"
      />

      <motion.div
        style={{ y: glowY }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[400px] sm:h-[500px] lg:h-[720px] w-[400px] sm:w-[500px] lg:w-[720px] bg-indigo-500/20 blur-[180px] rounded-full"
      />

      <div className="pointer-events-none absolute left-0 top-0 h-full w-1/3 bg-gradient-to-r from-indigo-900/40 to-transparent" />
      <div className="pointer-events-none absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-cyan-900/35 to-transparent" />
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 5 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-indigo-400/15 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{ y: [0, -12, 0], opacity: [0.15, 0.4, 0.15] }}
            transition={{
              duration: 8 + Math.random() * 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-14 items-center">
          <div className="text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8 sm:mb-10 flex flex-wrap justify-center lg:justify-start gap-4 sm:gap-8 text-xs sm:text-sm text-gray-300 tracking-wide"
            >
              <span>Mentor Guided</span>
              <span>•</span>
              <span>Structured Learning</span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">Practical Exposure</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl font-extrabold leading-[1.05] tracking-tight"
            >
              Building Your <br className="hidden lg:block" />
              Path to
            </motion.h1>

            <div className="relative h-16 sm:h-20 md:h-24 lg:h-[96px] mt-2 flex justify-center lg:justify-start items-center overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.span
                  key={words[index]}
                  initial={{ y: "100%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  exit={{ y: "-100%", opacity: 0 }}
                  transition={{ duration: 0.7, ease: "easeInOut" }}
                  className="absolute text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-500 to-cyan-400"
                >
                  {words[index]}
                </motion.span>
              </AnimatePresence>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.6 }}
              className="mt-6 sm:mt-8 max-w-2xl mx-auto lg:mx-0 text-base sm:text-lg text-gray-300 px-4 sm:px-0"
            >
              We design professional courses and internships that transform
              students into confident, skilled, and industry-ready professionals
              through expert mentorship and real-world exposure.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.35, duration: 0.5 }}
              className="mt-10 sm:mt-12 flex justify-center lg:justify-start gap-2 sm:gap-6 flex-wrap sm:flex-nowrap px-2 sm:px-0"
            >
              <motion.button
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 0 22px rgba(99,102,241,0.45)",
                }}
                whileTap={{ scale: 0.95 }}
                onClick={goToEnquiry}
                className="px-6 sm:px-10 py-3 sm:py-4 rounded-lg
             bg-indigo-500 text-sm sm:text-lg font-semibold
             shadow-xl shadow-indigo-500/30 whitespace-nowrap"
              >
                Enquire Now
              </motion.button>

              <button
                onClick={goToPrograms}
                className="px-6 sm:px-10 py-3 sm:py-4 rounded-lg
             border border-white/30 text-sm sm:text-lg
             hover:bg-white/10 transition whitespace-nowrap"
              >
                View Programs
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="mt-12 sm:mt-16 flex flex-wrap justify-center lg:justify-start gap-3 sm:gap-6 text-xs sm:text-sm text-gray-300 px-4 sm:px-0 "
            >
              {[
                "Hands-on Curriculum",
                "Real-World Exposure",
                "Career-Focused Learning",
                "Guided Learning",
              ].map((item) => (
                <div
                  key={item}
                  className="px-4 sm:px-6 py-2 sm:py-3 rounded-full bg-white/5 border border-white/10 backdrop-blur"
                >
                  {item}
                </div>
              ))}
            </motion.div>
          </div>

          <div className="hidden lg:flex justify-center">
            <motion.img
              src="/illustration.png"
              alt="Career Illustration"
              className="w-full max-w-lg object-contain"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.7 }}
            />
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.6 }}
          className="mt-16 sm:mt-20 lg:mt-28 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-10 max-w-5xl mx-auto px-4 sm:px-0"
        >
          {[
            ["250+", "Students Trained"],
            ["10+", "Programs Offered"],
            ["500+", "Hours of Courses"],
            ["100%", "Practical Focus"],
          ].map(([num, label]) => (
            <div key={label} className="text-center">
              <p className="text-2xl sm:text-3xl font-extrabold text-indigo-400">
                {num}
              </p>
              <p className="mt-2 text-xs sm:text-sm text-gray-400">{label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
